/**
 * aiUsageModel - every backend AI-usage payload → the shapes the panel draws.
 *
 * THE POINT OF THIS FILE. The `/ai-usage` read API is being built in parallel
 * (pons-backend-platform), and at the time this panel was written NO version
 * of it had shipped - not on `development`, not on any feature branch. So the
 * panel is written against the PROPOSED contract, and every assumption about
 * it is isolated here: the paths in `AI_USAGE_ENDPOINTS`, the field names in
 * `normaliseUsage` / `normaliseRunDetail`, the entity vocabulary in
 * `AIUsageEntityType`. When the backend lands, this is the only file that has
 * to agree with it - the panel, the panes and the four pages read the
 * normalised shape and never touch a raw payload.
 *
 * Everything is read TOLERANTLY on purpose: a missing `cached_tokens`, a token
 * count that arrives as a string, `runs` nested under `data` rather than at
 * the top level, `by_run_type` absent entirely - each degrades to a sensible
 * zero or an empty list rather than throwing inside a docked pane. A usage
 * readout that renders "0" for a field the backend renamed is a bug someone
 * can see and fix; one that blanks the pane with a TypeError is not.
 *
 * ── USAGE ONLY. NO COST. ──────────────────────────────────────────────
 * The normalised shapes carry tokens, run counts, models and durations —
 * and NOTHING about money. Every surface that reads this model is
 * customer-facing (the AI-usage pane on a matter, table, draft or review,
 * and the Settings usage sections), and our per-token cost is our economics,
 * not the customer's: a dollar figure there would be read as a bill. So
 * `cost_usd` is dropped HERE, at the boundary, rather than hidden at the
 * render sites - a backend that keeps sending it cannot leak it, because
 * there is no field on the normalised shape for it to land in and no
 * formatter left to draw it. `orgAIUsageModel` states the same rule for the
 * org-scoped routes.
 */

/** The entity vocabulary the read API is keyed by. */
export type AIUsageEntityType =
  | 'matter'
  | 'table'
  | 'draft'
  | 'review'
  | 'research'
  | 'template'
  | 'datasource'
  | 'contract';

/**
 * THE ONLY PLACE THE ROUTES ARE SPELLED.
 *
 * Per the proposed contract: a summary read keyed by `entity_type` +
 * `entity_id`, and a per-run drill-down under `/ai-usage/run/{run_id}`.
 */
export const AI_USAGE_ENDPOINTS = {
  summary: '/ai-usage',
  run: (runId: string) => `/ai-usage/run/${encodeURIComponent(runId)}`,
};

/* ── the normalised shapes ────────────────────────────────────────── */

/** The token quartet plus the total, on every level of the payload. */
export interface AIUsageTokens {
  prompt: number;
  cached: number;
  completion: number;
  reasoning: number;
  total: number;
}

export interface AIUsageTotals extends AIUsageTokens {
  runs: number;
}

/** One `by_run_type` bucket, with its label already resolved. */
export interface AIUsageBreakdownRow {
  runType: string;
  runs: number;
  totalTokens: number;
}

export interface AIUsageRun {
  id: string;
  service: string;
  runType: string;
  status: string;
  /**
   * The actor's UUID - resolved to a person by the panel, never rendered.
   *
   * A UUID since the metering tables moved off integer ids
   * (`organization_uuid` / `user_uuid`). There is deliberately NO fallback to
   * a legacy integer `user_id`: the org directory is keyed by uuid, so an int
   * could only ever fail to resolve and would render as an unattributed run
   * either way - while carrying a value that looks like it should have
   * worked. Same rule the research share code states: an int `user_id` must
   * never be picked, because it cannot match.
   */
  userUuid: string;
  startedAt: string;
  endedAt: string;
  durationMs: number;
  callCount: number;
  tokens: AIUsageTokens;
  /** `{ "gpt-4o": 12, … }` or a plain string - normalised to the map. */
  modelMix: Record<string, number>;
  docCount: number;
  inputBytes: number;
}

/** One call inside a run - the drill-down row. */
export interface AIUsageCall {
  id: string;
  deployment: string;
  step: string;
  tokens: AIUsageTokens;
  latencyMs: number;
  success: boolean;
}

export interface AIUsageSummary {
  entityType: string;
  entityId: string;
  /** What this entity's OWN runs consumed. Always present. */
  totals: AIUsageTotals;
  /**
   * The rolled-up total: this entity's runs PLUS everything linked to it.
   *
   * A matter is the only thing that has this - its `/ai-usage` response adds
   * `totals_including_linked` summing the drafts, tables, reviews and
   * research sessions attached to it. `null` for every other entity type, and
   * `null` for a matter whose backend has not shipped the field yet: it is
   * read as present-or-absent rather than defaulted to `totals`, so the panel
   * can tell "nothing linked" from "the backend did not say" and simply draw
   * nothing in the second case.
   */
  totalsIncludingLinked: AIUsageTotals | null;
  breakdown: AIUsageBreakdownRow[];
  runs: AIUsageRun[];
}

export interface AIUsageRunDetail {
  run: AIUsageRun | null;
  calls: AIUsageCall[];
}

/* ── tolerant readers ─────────────────────────────────────────────── */

/** A number from anything the backend might send, including `"0.0043"`. */
export const num = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

/** An object with fields - not an array, which `typeof` also calls "object". */
const isPlainObject = (value: unknown): boolean =>
  !!value && typeof value === 'object' && !Array.isArray(value);

/**
 * A scalar field as a string - and EMPTY for anything that is not one.
 *
 * `String({})` is `"[object Object]"`, which is not a value: it is a bug
 * printed into a label where a run type or a step name should be. Every
 * caller of this has a fallback for an empty field (`t('Unnamed run')`,
 * `t('Call')`, PersonCell's em-dash), so degrading to '' puts the honest
 * placeholder on screen instead. Objects and arrays are the shapes worth
 * catching: a backend that nests `{ value: … }` under a field that used to
 * be flat should read as "not said", not as punctuation.
 */
const str = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return '';
  return String(value);
};

/**
 * ANY thrown thing → a sentence a person can read. Never an object.
 *
 * Re-exported from the shared `utils/errorMessage` helper, which is where the
 * whole story of this bug (and the rule that every candidate must be a string)
 * now lives. Kept exported here because the pane, its hooks and its tests have
 * always reached for it under this name.
 */
export { readErrorMessage } from '../../../utils/errorMessage';

/**
 * The token quartet off any level of the payload.
 *
 * `total_tokens` is trusted when present and derived when not: a backend that
 * reports the parts but not the sum should still produce a total, and one
 * that reports a total which disagrees with its parts is reporting its own
 * accounting, which is the number to show.
 */
const readTokens = (raw: any): AIUsageTokens => {
  const prompt = num(raw?.prompt_tokens);
  const cached = num(raw?.cached_tokens);
  const completion = num(raw?.completion_tokens);
  const reasoning = num(raw?.reasoning_tokens);
  const reported = num(raw?.total_tokens);
  return {
    prompt,
    cached,
    completion,
    reasoning,
    total: reported || prompt + completion + reasoning,
  };
};

/**
 * `model_mix` in whichever shape it arrives.
 *
 * A map of deployment → call count is the contract; a bare string ("gpt-4o")
 * and a list of names are both accepted, since a mix of one model is the case
 * a backend is most likely to flatten.
 */
const readModelMix = (raw: any): Record<string, number> => {
  if (!raw) return {};
  if (typeof raw === 'string') return raw.trim() ? { [raw.trim()]: 0 } : {};
  if (Array.isArray(raw)) {
    return raw.reduce<Record<string, number>>((acc, name) => {
      const key = str(name).trim();
      if (key) acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }
  if (typeof raw === 'object') {
    return Object.entries(raw).reduce<Record<string, number>>((acc, [key, value]) => {
      if (key) acc[key] = num(value);
      return acc;
    }, {});
  }
  return {};
};

const readRun = (raw: any): AIUsageRun => ({
  id: str(raw?.id),
  service: str(raw?.service),
  runType: str(raw?.run_type),
  status: str(raw?.status),
  userUuid: str(raw?.user_uuid),
  startedAt: str(raw?.started_at),
  endedAt: str(raw?.ended_at),
  durationMs: num(raw?.duration_ms),
  callCount: num(raw?.call_count),
  tokens: readTokens(raw),
  modelMix: readModelMix(raw?.model_mix),
  docCount: num(raw?.doc_count),
  inputBytes: num(raw?.input_bytes),
});

const readCall = (raw: any): AIUsageCall => ({
  id: str(raw?.id),
  deployment: str(raw?.deployment),
  step: str(raw?.step),
  tokens: readTokens(raw),
  latencyMs: num(raw?.latency_ms),
  // Absent reads as success: a call the backend recorded without a verdict
  // is not a failure, and a list of red rows would be a lie about the run.
  success: raw?.success === undefined || raw?.success === null ? true : !!raw.success,
});

/**
 * The envelope, unwrapped.
 *
 * The platform's own convention is `{ success, data }` (see CLAUDE.md §16.15),
 * but several read endpoints answer with the object at the top level. Both are
 * accepted rather than betting on which one this route picks.
 */
const unwrap = (payload: any): any => {
  if (!payload || typeof payload !== 'object') return {};
  if (payload.data && typeof payload.data === 'object') return payload.data;
  return payload;
};

/**
 * `by_run_type`, as rows sorted by what they consumed.
 *
 * Sorted by tokens and then run count because the breakdown is read to answer
 * "what is this item using AI on", and the heaviest bucket is the answer.
 * Derived from the runs when the backend sends no `by_run_type` at all, so
 * the breakdown survives that field being renamed or dropped.
 */
const readBreakdown = (raw: any, runs: AIUsageRun[]): AIUsageBreakdownRow[] => {
  const fromPayload =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? Object.entries(raw).map(([runType, bucket]: [string, any]) => ({
          runType,
          runs: num(bucket?.runs),
          totalTokens: num(bucket?.total_tokens),
        }))
      : [];

  const rows = fromPayload.length ? fromPayload : deriveBreakdown(runs);
  return rows.sort(
    (a, b) => b.totalTokens - a.totalTokens || b.runs - a.runs
      || a.runType.localeCompare(b.runType)
  );
};

/** The same buckets, added up from the run list. */
const deriveBreakdown = (runs: AIUsageRun[]): AIUsageBreakdownRow[] => {
  const byType = new Map<string, AIUsageBreakdownRow>();
  runs.forEach((run) => {
    const key = run.runType || 'unknown';
    const row = byType.get(key) || { runType: key, runs: 0, totalTokens: 0 };
    row.runs += 1;
    row.totalTokens += run.tokens.total;
    byType.set(key, row);
  });
  return Array.from(byType.values());
};

/** Totals from the payload, or added up from the runs when it sends none. */
const readTotals = (raw: any, runs: AIUsageRun[]): AIUsageTotals => {
  if (raw && typeof raw === 'object') {
    const tokens = readTokens(raw);
    return {
      ...tokens,
      runs: num(raw.runs) || runs.length,
    };
  }
  return runs.reduce<AIUsageTotals>(
    (acc, run) => ({
      prompt: acc.prompt + run.tokens.prompt,
      cached: acc.cached + run.tokens.cached,
      completion: acc.completion + run.tokens.completion,
      reasoning: acc.reasoning + run.tokens.reasoning,
      total: acc.total + run.tokens.total,
      runs: acc.runs + 1,
    }),
    { prompt: 0, cached: 0, completion: 0, reasoning: 0, total: 0, runs: 0 }
  );
};

/** Newest first - a usage list is read from the run that just happened. */
const newestFirst = (runs: AIUsageRun[]): AIUsageRun[] =>
  [...runs].sort((a, b) => (b.startedAt || '').localeCompare(a.startedAt || ''));

export const normaliseUsage = (payload: any): AIUsageSummary => {
  const body = unwrap(payload);
  const runs = newestFirst(
    (Array.isArray(body?.runs) ? body.runs : []).map(readRun)
  );
  return {
    entityType: str(body?.entity_type),
    entityId: str(body?.entity_id),
    totals: readTotals(body?.totals, runs),
    /*
      Present-or-absent, never derived. The rolled-up figure can only come
      from the backend - the panel holds one entity's runs and has no way to
      add up a matter's drafts and tables itself - so anything other than a
      real object here is `null`, and the secondary line is not drawn.
    */
    totalsIncludingLinked: isPlainObject(body?.totals_including_linked)
      ? readTotals(body.totals_including_linked, [])
      : null,
    breakdown: readBreakdown(body?.totals?.by_run_type, runs),
    runs,
  };
};

export const normaliseRunDetail = (payload: any): AIUsageRunDetail => {
  const body = unwrap(payload);
  // The run may be the body itself or nested under `run` - both are read.
  const rawRun = body?.run && typeof body.run === 'object' ? body.run : body;
  return {
    run: rawRun && rawRun.id !== undefined ? readRun(rawRun) : null,
    calls: (Array.isArray(body?.calls) ? body.calls : []).map(readCall),
  };
};
